/**
 * UsersAdmin — SerSo-internal user management table.
 *
 * Sources data from `GET /users` (paginated, see backend schemas/common.py).
 * Mutations: `POST /users`, `PATCH /users/{id}`, `POST /users/{id}/reset-password`,
 * `POST /users/{id}/disable`. All user-facing copy is Dutch (NL).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Plus, UserMinus, UserPlus } from 'lucide-react';

import { apiGet, apiRequest, ApiError } from '../../lib/api';
import { formatRelativeTime } from '../../lib/locale';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { StatusDot } from '../../components/StatusDot';
import { EmptyState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';
import { toast } from '../../lib/toast';

// ---------------------------------------------------------------------------
// Types (mirror backend schemas/user.py — keep local until api.gen.ts is live)
// ---------------------------------------------------------------------------

type PlatformRole = 'admin' | 'operator' | 'analyst' | 'readonly';

interface UserOut {
  id: string;
  email: string;
  full_name: string | null;
  platform_role: PlatformRole;
  is_active: boolean;
  totp_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface UsersPage {
  data: UserOut[];
  meta: { page: number; per_page: number; total: number; pages: number };
}

interface UserCreateIn {
  email: string;
  full_name: string | null;
  platform_role: PlatformRole;
  password: string;
}

interface UserUpdateIn {
  email?: string;
  full_name?: string | null;
  platform_role?: PlatformRole;
}

const ROLE_OPTIONS: PlatformRole[] = ['admin', 'operator', 'analyst', 'readonly'];
const ROLE_LABEL_NL: Record<PlatformRole, string> = {
  admin: 'Admin',
  operator: 'Operator',
  analyst: 'Analist',
  readonly: 'Alleen-lezen',
};

const ROLE_TONE: Record<PlatformRole, 'info' | 'warn' | 'good' | 'neutral'> = {
  admin: 'warn',
  operator: 'info',
  analyst: 'good',
  readonly: 'neutral',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function UsersAdmin(): JSX.Element {
  const qc = useQueryClient();
  const [editTarget, setEditTarget] = useState<UserOut | null>(null);
  const [tempPasswordFor, setTempPasswordFor] = useState<{
    user: UserOut;
    password: string;
  } | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<UserOut | null>(null);
  const [creating, setCreating] = useState(false);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => apiGet<UsersPage>('/users?per_page=200'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (user: UserOut) => {
      const res = await apiRequest<{ data: { temporary_password: string } }>(
        `/users/${user.id}/reset-password`,
        { method: 'POST' },
      );
      return { user, password: res.data.temporary_password };
    },
    onSuccess: (result) => {
      setTempPasswordFor(result);
      toast({
        tone: 'success',
        message: `Tijdelijk wachtwoord gegenereerd voor ${result.user.email}.`,
      });
    },
    onError: (err) => toast({ tone: 'error', message: formatApiError(err) }),
  });

  const disableMutation = useMutation({
    mutationFn: async (user: UserOut) => {
      await apiRequest(`/users/${user.id}/disable`, { method: 'POST' });
      return user;
    },
    onSuccess: (user) => {
      setConfirmDisable(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast({ tone: 'success', message: `${user.email} is gedeactiveerd.` });
    },
    onError: (err) => toast({ tone: 'error', message: formatApiError(err) }),
  });

  return (
    <div className="space-y-4 px-6 py-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Gebruikers</h1>
          <p className="mt-0.5 text-[12px] text-slate-500">
            Beheer SerSo-interne portalgebruikers (admin, operator, analist, alleen-lezen).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-brand-700"
        >
          <Plus size={14} aria-hidden /> Gebruiker toevoegen
        </button>
      </header>

      <Card>
        <CardHeader
          title="Alle gebruikers"
          subtitle={usersQuery.data ? `${usersQuery.data.meta.total} gebruiker(s)` : 'Laden…'}
        />

        {usersQuery.isLoading ? (
          <div className="flex justify-center px-6 py-12">
            <Spinner />
          </div>
        ) : usersQuery.isError ? (
          <EmptyState
            title="Gebruikerslijst niet beschikbaar"
            description={formatApiError(usersQuery.error)}
          />
        ) : !usersQuery.data || usersQuery.data.data.length === 0 ? (
          <EmptyState
            title="Nog geen gebruikers"
            description="Voeg de eerste SerSo-medewerker toe via 'Gebruiker toevoegen'."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">E-mail</th>
                  <th className="px-3 py-2">Naam</th>
                  <th className="px-3 py-2">Rol</th>
                  <th className="px-3 py-2">Actief</th>
                  <th className="px-3 py-2">TOTP</th>
                  <th className="px-3 py-2">Laatste login</th>
                  <th className="px-3 py-2 text-right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {usersQuery.data.data.map((user) => (
                  <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-slate-900">{user.email}</td>
                    <td className="px-3 py-2 text-slate-700">{user.full_name ?? '—'}</td>
                    <td className="px-3 py-2">
                      <Badge tone={ROLE_TONE[user.platform_role]}>
                        {ROLE_LABEL_NL[user.platform_role]}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot status={user.is_active ? 'good' : 'fail'} />
                        <span className="text-slate-600">{user.is_active ? 'Ja' : 'Nee'}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot status={user.totp_enabled ? 'good' : 'warn'} />
                        <span className="text-slate-600">{user.totp_enabled ? 'Ja' : 'Nee'}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">
                      {user.last_login_at ? formatRelativeTime(user.last_login_at) : 'Nooit'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          aria-label={`Bewerk gebruiker ${user.email}`}
                          onClick={() => setEditTarget(user)}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        >
                          <Pencil size={12} aria-hidden /> Bewerk
                        </button>
                        <button
                          type="button"
                          aria-label={`Reset wachtwoord voor ${user.email}`}
                          disabled={resetPasswordMutation.isPending}
                          onClick={() => resetPasswordMutation.mutate(user)}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
                        >
                          <KeyRound size={12} aria-hidden /> Reset wachtwoord
                        </button>
                        <button
                          type="button"
                          aria-label={`Deactiveer gebruiker ${user.email}`}
                          disabled={!user.is_active}
                          onClick={() => setConfirmDisable(user)}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] text-red-700 hover:bg-red-50 disabled:opacity-30"
                        >
                          <UserMinus size={12} aria-hidden /> Deactiveer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
          }}
        />
      )}

      {editTarget && (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
          }}
        />
      )}

      {tempPasswordFor && (
        <TempPasswordModal
          email={tempPasswordFor.user.email}
          password={tempPasswordFor.password}
          onClose={() => setTempPasswordFor(null)}
        />
      )}

      {confirmDisable && (
        <ConfirmDisableModal
          user={confirmDisable}
          pending={disableMutation.isPending}
          onCancel={() => setConfirmDisable(null)}
          onConfirm={() => disableMutation.mutate(confirmDisable)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modals — kept inline to limit surface area; lifted out only when reused.
// ---------------------------------------------------------------------------

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="bg-slate-900/40 fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluit dialoog"
            className="text-slate-400 hover:text-slate-700"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [form, setForm] = useState<UserCreateIn>({
    email: '',
    full_name: '',
    platform_role: 'readonly',
    password: '',
  });

  const create = useMutation({
    mutationFn: async (payload: UserCreateIn) =>
      apiRequest<{ data: UserOut }>('/users', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      toast({ tone: 'success', message: 'Gebruiker aangemaakt.' });
      onCreated();
    },
    onError: (err) => toast({ tone: 'error', message: formatApiError(err) }),
  });

  return (
    <ModalShell title="Nieuwe gebruiker toevoegen" onClose={onClose}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate({
            ...form,
            full_name: form.full_name?.trim() ? form.full_name : null,
          });
        }}
      >
        <Field label="E-mailadres" required>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="Volledige naam">
          <input
            type="text"
            value={form.full_name ?? ''}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="Rol" required>
          <select
            value={form.platform_role}
            onChange={(e) => setForm({ ...form, platform_role: e.target.value as PlatformRole })}
            className={inputCls}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL_NL[r]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Initieel wachtwoord" required>
          <input
            type="text"
            required
            minLength={12}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputCls}
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Minstens 12 tekens. De gebruiker wordt verzocht dit bij eerste login te wijzigen.
          </p>
        </Field>
        <ModalFooter onCancel={onClose} submitLabel="Aanmaken" submitting={create.isPending} />
      </form>
    </ModalShell>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserOut;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [form, setForm] = useState<UserUpdateIn>({
    full_name: user.full_name,
    platform_role: user.platform_role,
  });

  const update = useMutation({
    mutationFn: async (payload: UserUpdateIn) =>
      apiRequest<{ data: UserOut }>(`/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      toast({ tone: 'success', message: 'Gebruiker bijgewerkt.' });
      onSaved();
    },
    onError: (err) => toast({ tone: 'error', message: formatApiError(err) }),
  });

  return (
    <ModalShell title={`Bewerk: ${user.email}`} onClose={onClose}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate({
            full_name: form.full_name?.trim() ? form.full_name : null,
            platform_role: form.platform_role,
          });
        }}
      >
        <Field label="Volledige naam">
          <input
            type="text"
            value={form.full_name ?? ''}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="Rol" required>
          <select
            value={form.platform_role}
            onChange={(e) => setForm({ ...form, platform_role: e.target.value as PlatformRole })}
            className={inputCls}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL_NL[r]}
              </option>
            ))}
          </select>
        </Field>
        <ModalFooter onCancel={onClose} submitLabel="Opslaan" submitting={update.isPending} />
      </form>
    </ModalShell>
  );
}

function TempPasswordModal({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <ModalShell title="Tijdelijk wachtwoord" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-[12px] text-slate-700">
          Onderstaand wachtwoord wordt <strong>eenmalig</strong> getoond. Deel het veilig met{' '}
          <span className="font-mono">{email}</span>. Hen wordt gevraagd het bij eerste login te
          wijzigen.
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 select-all rounded border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[13px] text-slate-900">
            {password}
          </code>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(password);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            }}
            className="rounded bg-slate-100 px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-200"
          >
            {copied ? 'Gekopieerd' : 'Kopieer'}
          </button>
        </div>
        <p className="text-[11px] text-slate-500">
          SerSo bewaart dit wachtwoord niet. Sluit deze dialoog pas als je het hebt overgedragen.
        </p>
        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-brand-700"
          >
            Sluiten
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ConfirmDisableModal({
  user,
  pending,
  onCancel,
  onConfirm,
}: {
  user: UserOut;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <ModalShell title="Gebruiker deactiveren" onClose={onCancel}>
      <div className="space-y-3">
        <p className="text-[12px] text-slate-700">
          Weet je zeker dat je <strong className="font-mono">{user.email}</strong> wilt deactiveren?
          De gebruiker kan niet meer inloggen, maar audit-historie blijft bewaard.
        </p>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-100"
          >
            Annuleer
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded bg-red-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            <UserMinus size={12} aria-hidden /> Deactiveer
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

const inputCls =
  'w-full px-2.5 py-1.5 text-[12px] bg-white border border-slate-300 rounded focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600';

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-slate-700">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>
      {children}
    </label>
  );
}

function ModalFooter({
  onCancel,
  submitLabel,
  submitting,
}: {
  onCancel: () => void;
  submitLabel: string;
  submitting: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onCancel}
        className="rounded px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-100"
      >
        Annuleer
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-brand-700 disabled:opacity-50"
      >
        <UserPlus size={12} aria-hidden /> {submitLabel}
      </button>
    </div>
  );
}

function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.problem?.detail ?? err.problem?.title ?? `Fout (${err.status}).`;
  }
  if (err instanceof Error) return err.message;
  return 'Onbekende fout.';
}
